import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import type {
  ChatMessage,
  ContentBlock,
  Citation,
  PdfExportOptions,
  VisualizationData,
} from '../types';
import { formatNumber } from './utils';

// PDF Configuration - matching Nebula web styling
const PDF_CONFIG = {
  a4: { width: 595.28, height: 841.89 },
  letter: { width: 612, height: 792 },
  margin: { top: 40, right: 40, bottom: 40, left: 40 },
  fontSize: {
    title: 16,
    heading: 12,
    body: 10,
    small: 8,
  },
  lineHeight: 1.6,
  colors: {
    primary: '#3B82F6',      // Blue for user elements
    secondary: '#6366f1',    // Indigo for Nebula
    text: '#1f2937',         // Dark text (headings)
    bodyText: '#374151',     // Softer body text for readability
    muted: '#6b7280',        // Gray text
    border: '#e5e7eb',       // Light border
    background: '#F1F0EC',   // Nova dashboard background
    cardBg: '#ffffff',       // White card background
    tableBg: '#F8F7F2',      // Table background
    tableHeader: '#f3f4f6',  // Table header background
  },
};

interface RenderContext {
  pdf: jsPDF;
  pageWidth: number;
  pageHeight: number;
  contentWidth: number;
  margin: typeof PDF_CONFIG.margin;
  currentY: number;
}

// Format timestamp for display
function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Sanitize text for jsPDF rendering.
// 1. Strip HTML/XML tags (e.g. <answer>, </answer>) that leak from AI responses.
//    The PDF renderer has no HTML support — ReactMarkdown strips these in the web UI
//    but the PDF code path needs to do it explicitly.
// 2. Replace Unicode characters outside Windows-1252 with ASCII equivalents,
//    since jsPDF's built-in Helvetica corrupts character spacing for unsupported chars.
function sanitizeForPdf(text: string): string {
  return text
    // Strip HTML/XML tags — AI wrapper tags like <answer>, </answer>, <response>, etc.
    // Safe because: code blocks are handled separately before reaching this function,
    // and legitimate < > in prose (like "5 < 10") don't form valid tag patterns.
    .replace(/<\/?[a-zA-Z][a-zA-Z0-9_-]*(?:\s[^>]*)?\/?>/g, '')
    // Arrows
    .replace(/[→➔➜➝➞⟶⟹⇒⇨➡]/g, '->')
    .replace(/[←⟵⟸⇐⇦]/g, '<-')
    .replace(/[↑⬆]/g, '^')
    .replace(/[↓⬇]/g, 'v')
    .replace(/[↔⟷⇔]/g, '<->')
    // Dashes
    .replace(/—/g, '--')
    .replace(/–/g, '-')
    // Smart quotes
    .replace(/["\u201C\u201D]/g, '"')
    .replace(/['\u2018\u2019]/g, "'")
    // Ellipsis
    .replace(/…/g, '...')
    // Check/cross marks
    .replace(/[✓✔☑✅]/g, '[x]')
    .replace(/[✗✘☒❌]/g, '[ ]')
    // Math symbols
    .replace(/×/g, 'x')
    .replace(/÷/g, '/')
    .replace(/≈/g, '~')
    .replace(/≠/g, '!=')
    .replace(/[≤≦]/g, '<=')
    .replace(/[≥≧]/g, '>=')
    // Common symbols
    .replace(/©/g, '(c)')
    .replace(/®/g, '(R)')
    .replace(/™/g, '(TM)')
    // Misc
    .replace(/[▶►▸]/g, '>')
    .replace(/[◀◄◂]/g, '<')
    .replace(/[★☆]/g, '*')
    .replace(/[●◉⬤]/g, 'o')
    // Strip any remaining characters outside Windows-1252 range
    // that would corrupt jsPDF rendering (keep bullet \u2022 = 0x95 in CP1252)
    .replace(/[^\x00-\xFF\u2022]/g, '');
}

// Check if page break is needed and add new page if necessary
function ensureSpace(ctx: RenderContext, neededHeight: number): void {
  if (ctx.currentY + neededHeight > ctx.pageHeight - ctx.margin.bottom) {
    ctx.pdf.addPage();
    ctx.currentY = ctx.margin.top;
  }
}

// Add text with word wrapping
function addWrappedText(
  ctx: RenderContext,
  text: string,
  fontSize: number,
  color: string,
  indent: number = 0
): void {
  ctx.pdf.setFontSize(fontSize);
  ctx.pdf.setTextColor(color);

  const maxWidth = ctx.contentWidth - indent;
  const lines = ctx.pdf.splitTextToSize(sanitizeForPdf(text), maxWidth);
  const lineHeight = fontSize * PDF_CONFIG.lineHeight;

  for (const line of lines) {
    ensureSpace(ctx, lineHeight);
    ctx.pdf.text(line, ctx.margin.left + indent, ctx.currentY);
    ctx.currentY += lineHeight;
  }
}

// Text segment with styling
interface TextSegment {
  text: string;
  bold: boolean;
  italic: boolean;
}

// Parse inline markdown and return styled segments
function parseInlineMarkdown(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  // Match bold (**text** or __text__), italic (*text* or _text_), or plain text
  const regex = /(\*\*(.+?)\*\*|__(.+?)__|(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|(?<!_)_(?!_)(.+?)(?<!_)_(?!_))/g;

  let lastIndex = 0;
  let match;

  // Simplified approach: handle **bold** patterns
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let remaining = text;

  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before the match as plain
    if (match.index > lastIndex) {
      segments.push({
        text: text.slice(lastIndex, match.index),
        bold: false,
        italic: false,
      });
    }
    // Add the bold text
    segments.push({
      text: match[1],
      bold: true,
      italic: false,
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      text: text.slice(lastIndex),
      bold: false,
      italic: false,
    });
  }

  // If no segments were created, return the original text
  if (segments.length === 0) {
    segments.push({ text, bold: false, italic: false });
  }

  return segments;
}

// Render a line with inline markdown formatting
// Uses word-by-word wrapping that measures each word in its correct font
// to avoid overlap issues when mixing normal and bold text
function renderFormattedLine(
  ctx: RenderContext,
  text: string,
  fontSize: number,
  color: string,
  indent: number = 0
): void {
  const segments = parseInlineMarkdown(text);
  const lineHeight = fontSize * PDF_CONFIG.lineHeight;
  const maxWidth = ctx.contentWidth - indent;
  const startX = ctx.margin.left + indent;

  ctx.pdf.setFontSize(fontSize);
  ctx.pdf.setTextColor(color);

  // Build styled token list with accurate per-font measurements
  interface StyledToken {
    text: string;
    bold: boolean;
    width: number;
  }

  const tokens: StyledToken[] = [];

  for (const segment of segments) {
    ctx.pdf.setFont('helvetica', segment.bold ? 'bold' : 'normal');
    // Split into words and whitespace tokens, preserving each
    const parts = segment.text.split(/(\s+)/);
    for (const part of parts) {
      if (part.length > 0) {
        tokens.push({
          text: part,
          bold: segment.bold,
          width: ctx.pdf.getTextWidth(part),
        });
      }
    }
  }

  // Build lines with word wrapping
  const lines: StyledToken[][] = [];
  let currentLine: StyledToken[] = [];
  let lineWidth = 0;

  for (const token of tokens) {
    if (currentLine.length > 0 && lineWidth + token.width > maxWidth) {
      lines.push(currentLine);
      currentLine = [];
      lineWidth = 0;
      // Skip leading whitespace on new line
      if (token.text.trim() === '') continue;
    }

    // If a single token exceeds maxWidth, break it into characters
    if (token.width > maxWidth && token.text.trim() !== '') {
      ctx.pdf.setFont('helvetica', token.bold ? 'bold' : 'normal');
      const chars = token.text.split('');
      let chunk = '';
      let chunkWidth = 0;
      for (const char of chars) {
        const charWidth = ctx.pdf.getTextWidth(char);
        if (chunkWidth + charWidth > maxWidth && chunk.length > 0) {
          if (currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = [];
          }
          currentLine.push({ text: chunk, bold: token.bold, width: chunkWidth });
          lines.push(currentLine);
          currentLine = [];
          lineWidth = 0;
          chunk = '';
          chunkWidth = 0;
        }
        chunk += char;
        chunkWidth += charWidth;
      }
      if (chunk.length > 0) {
        currentLine.push({ text: chunk, bold: token.bold, width: chunkWidth });
        lineWidth += chunkWidth;
      }
      continue;
    }

    currentLine.push(token);
    lineWidth += token.width;
  }
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  // Render each line with correct font per token
  for (const line of lines) {
    ensureSpace(ctx, lineHeight);
    let xPos = startX;
    for (const token of line) {
      ctx.pdf.setFont('helvetica', token.bold ? 'bold' : 'normal');
      ctx.pdf.text(token.text, xPos, ctx.currentY);
      xPos += token.width;
    }
    ctx.currentY += lineHeight;
  }

  ctx.pdf.setFont('helvetica', 'normal');
}

// Check if a line is a markdown table row
function isTableRow(line: string): boolean {
  return line.trim().startsWith('|') && line.trim().endsWith('|');
}

// Check if a line is a table separator (|---|---|)
function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  // Check if all cells contain only dashes, colons, and spaces
  const cells = trimmed.split('|').slice(1, -1);
  return cells.every(cell => /^[\s\-:]+$/.test(cell));
}

// Strip markdown from text (for table cells)
function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')  // Bold **text**
    .replace(/\*([^*]+)\*/g, '$1')       // Italic *text*
    .replace(/__([^_]+)__/g, '$1')       // Bold __text__
    .replace(/_([^_]+)_/g, '$1')         // Italic _text_
    .replace(/`([^`]+)`/g, '$1');        // Inline code `text`
}

// Parse markdown table rows into cells
function parseTableRow(line: string): string[] {
  return line
    .split('|')
    .slice(1, -1) // Remove first and last empty elements
    .map(cell => stripMarkdownFormatting(cell.trim()));
}

// Render a markdown table (matching web design)
function renderMarkdownTable(ctx: RenderContext, tableLines: string[]): void {
  if (tableLines.length < 2) return;

  // Parse header
  const headers = parseTableRow(tableLines[0]);

  // Find data rows (skip separator line)
  const dataRows: string[][] = [];
  for (let i = 1; i < tableLines.length; i++) {
    if (!isTableSeparator(tableLines[i])) {
      dataRows.push(parseTableRow(tableLines[i]));
    }
  }

  if (headers.length === 0) return;

  // Table configuration
  const cellPadding = 6;
  const headerHeight = 24;
  const rowHeight = 20;
  const colCount = headers.length;
  const borderRadius = 4;

  // Calculate column widths - use available width
  const colWidth = ctx.contentWidth / colCount;
  const tableWidth = ctx.contentWidth;

  // Check if table fits, otherwise add page
  const tableHeight = headerHeight + dataRows.length * rowHeight + 16;
  ensureSpace(ctx, tableHeight);

  ctx.currentY += 12;
  const tableStartY = ctx.currentY;

  // Draw table border/background
  ctx.pdf.setFillColor(PDF_CONFIG.colors.cardBg);
  ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
  ctx.pdf.roundedRect(ctx.margin.left, ctx.currentY, tableWidth, tableHeight - 16, borderRadius, borderRadius, 'FD');

  // Draw header background
  ctx.pdf.setFillColor(PDF_CONFIG.colors.tableHeader);
  ctx.pdf.roundedRect(ctx.margin.left, ctx.currentY, tableWidth, headerHeight, borderRadius, borderRadius, 'F');
  // Cover bottom corners of header
  ctx.pdf.rect(ctx.margin.left, ctx.currentY + headerHeight - borderRadius, tableWidth, borderRadius, 'F');

  // Draw header text
  ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
  ctx.pdf.setFont('helvetica', 'bold');
  ctx.pdf.setTextColor(PDF_CONFIG.colors.text);

  headers.forEach((header, i) => {
    const x = ctx.margin.left + i * colWidth + cellPadding;
    const maxChars = Math.floor((colWidth - cellPadding * 2) / 4);
    const displayText = header.length > maxChars ? header.slice(0, maxChars) + '...' : header;
    ctx.pdf.text(displayText, x, ctx.currentY + headerHeight / 2 + 3);
  });

  // Draw header bottom border
  ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
  ctx.pdf.line(ctx.margin.left, ctx.currentY + headerHeight, ctx.margin.left + tableWidth, ctx.currentY + headerHeight);

  ctx.currentY += headerHeight;

  // Draw data rows
  ctx.pdf.setFont('helvetica', 'normal');

  dataRows.forEach((row, rowIndex) => {
    // Draw row bottom border
    ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
    if (rowIndex < dataRows.length - 1) {
      ctx.pdf.line(ctx.margin.left, ctx.currentY + rowHeight, ctx.margin.left + tableWidth, ctx.currentY + rowHeight);
    }

    // Draw cells
    ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
    ctx.pdf.setTextColor(PDF_CONFIG.colors.text);

    row.forEach((cell, i) => {
      if (i < colCount) {
        const x = ctx.margin.left + i * colWidth + cellPadding;
        const maxChars = Math.floor((colWidth - cellPadding * 2) / 4);
        const displayText = cell.length > maxChars ? cell.slice(0, maxChars) + '...' : cell;
        ctx.pdf.text(displayText, x, ctx.currentY + rowHeight / 2 + 3);
      }
    });

    ctx.currentY += rowHeight;
  });

  ctx.currentY += 12;
}

// Parse and render markdown-like content
function renderMarkdownContent(
  ctx: RenderContext,
  content: string,
  baseIndent: number = 0
): void {
  // Sanitize all content upfront to prevent jsPDF Unicode rendering issues
  const lines = sanitizeForPdf(content).split('\n');
  let inCodeBlock = false;
  let codeBlockLines: string[] = [];
  let tableLines: string[] = [];
  let lastLineWasEmpty = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code block handling
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        // End code block - render accumulated code
        renderCodeBlock(ctx, codeBlockLines.join('\n'), baseIndent);
        codeBlockLines = [];
      }
      inCodeBlock = !inCodeBlock;
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    // Table handling - collect consecutive table rows
    if (isTableRow(line)) {
      tableLines.push(line);
      // Check if next line is also a table row
      if (i + 1 >= lines.length || !isTableRow(lines[i + 1])) {
        // End of table, render it
        if (tableLines.length >= 2) {
          renderMarkdownTable(ctx, tableLines);
        }
        tableLines = [];
      }
      continue;
    }

    // Heading detection — use regex with optional space after # markers
    // to handle cases where emoji stripping removes the character between ## and text
    // (e.g. "## 🔟Collaboration..." → "##Collaboration..." after sanitization)
    // Horizontal rule (---, ***, ___)
    if (/^[\s]*[-*_][\s]*[-*_][\s]*[-*_][\s]*$/.test(line)) {
      ctx.currentY += 12;
      ensureSpace(ctx, 2);
      ctx.pdf.setDrawColor('#9ca3af');
      ctx.pdf.line(ctx.margin.left + baseIndent, ctx.currentY, ctx.margin.left + ctx.contentWidth, ctx.currentY);
      ctx.currentY += 12;
      continue;
    }

    const h3Match = line.match(/^###\s?(.*\S.*)/);
    if (h3Match) {
      ctx.currentY += 12;
      ctx.pdf.setFont('helvetica', 'bold');
      addWrappedText(ctx, h3Match[1].replace(/\*\*/g, ''), 11, PDF_CONFIG.colors.text, baseIndent);
      ctx.pdf.setFont('helvetica', 'normal');
      ctx.currentY += 2;
      continue;
    }

    const h2Match = line.match(/^##\s?(.*\S.*)/);
    if (h2Match) {
      ctx.currentY += 18;
      ctx.pdf.setFont('helvetica', 'bold');
      addWrappedText(ctx, h2Match[1].replace(/\*\*/g, ''), 13, PDF_CONFIG.colors.text, baseIndent);
      ctx.pdf.setFont('helvetica', 'normal');
      ctx.currentY += 3;
      continue;
    }

    const h1Match = line.match(/^#\s?(.*\S.*)/);
    if (h1Match) {
      ctx.currentY += 24;
      ctx.pdf.setFont('helvetica', 'bold');
      addWrappedText(ctx, h1Match[1].replace(/\*\*/g, ''), 16, PDF_CONFIG.colors.text, baseIndent);
      ctx.pdf.setFont('helvetica', 'normal');
      ctx.currentY += 4;
      continue;
    }

    // Bullet points — proper hierarchy with hanging indent
    if (line.match(/^[\s]*[-*]\s/)) {
      const leadingSpaces = (line.match(/^[\s]*/)?.[0].length || 0);
      const nestLevel = Math.floor(leadingSpaces / 2);
      const bulletText = line.replace(/^[\s]*[-*]\s/, '');
      const indentPerLevel = 15;
      const totalIndent = baseIndent + nestLevel * indentPerLevel;

      // Different bullet character per nesting level
      const bulletChar = nestLevel === 0 ? '\u2022' : '\u2013'; // • for L0, – for L1+
      const lineHeight = PDF_CONFIG.fontSize.body * PDF_CONFIG.lineHeight;

      // Ensure space and draw bullet character
      ensureSpace(ctx, lineHeight);
      ctx.pdf.setFontSize(PDF_CONFIG.fontSize.body);
      ctx.pdf.setTextColor(PDF_CONFIG.colors.muted);
      ctx.pdf.setFont('helvetica', 'normal');
      ctx.pdf.text(bulletChar, ctx.margin.left + totalIndent, ctx.currentY);

      // Render text with hanging indent (text aligns past bullet on wrapped lines)
      const bulletWidth = ctx.pdf.getTextWidth(bulletChar);
      const textIndent = totalIndent + bulletWidth + 4;
      renderFormattedLine(ctx, bulletText, PDF_CONFIG.fontSize.body, PDF_CONFIG.colors.bodyText, textIndent);

      // Small gap between bullet items for breathing room
      ctx.currentY += 3;
      continue;
    }

    // Numbered lists — proper hierarchy with hanging indent
    if (line.match(/^[\s]*\d+\.\s/)) {
      const leadingSpaces = (line.match(/^[\s]*/)?.[0].length || 0);
      const nestLevel = Math.floor(leadingSpaces / 2);
      const numberMatch = line.match(/^[\s]*(\d+\.)\s(.*)/);
      if (numberMatch) {
        const numberLabel = numberMatch[1];
        const listText = numberMatch[2];
        const indentPerLevel = 15;
        const totalIndent = baseIndent + nestLevel * indentPerLevel;
        const lineHeight = PDF_CONFIG.fontSize.body * PDF_CONFIG.lineHeight;

        // Ensure space and draw number label
        ensureSpace(ctx, lineHeight);
        ctx.pdf.setFontSize(PDF_CONFIG.fontSize.body);
        ctx.pdf.setTextColor(PDF_CONFIG.colors.bodyText);
        ctx.pdf.setFont('helvetica', 'normal');
        ctx.pdf.text(numberLabel, ctx.margin.left + totalIndent, ctx.currentY);

        // Render text with hanging indent
        const numberWidth = ctx.pdf.getTextWidth(numberLabel);
        const textIndent = totalIndent + numberWidth + 4;
        renderFormattedLine(ctx, listText, PDF_CONFIG.fontSize.body, PDF_CONFIG.colors.bodyText, textIndent);

        ctx.currentY += 3;
      } else {
        renderFormattedLine(ctx, line.trim(), PDF_CONFIG.fontSize.body, PDF_CONFIG.colors.bodyText, baseIndent);
      }
      continue;
    }

    // Empty line — collapse consecutive blanks into one
    if (line.trim() === '') {
      if (!lastLineWasEmpty) {
        ctx.currentY += 6;
        lastLineWasEmpty = true;
      }
      continue;
    }
    lastLineWasEmpty = false;

    // Regular text with formatting
    renderFormattedLine(ctx, line, PDF_CONFIG.fontSize.body, PDF_CONFIG.colors.bodyText, baseIndent);
  }
}

// Render code block with background (matching web design)
function renderCodeBlock(ctx: RenderContext, code: string, indent: number): void {
  const lines = sanitizeForPdf(code).split('\n');
  const lineHeight = PDF_CONFIG.fontSize.small * 1.4;
  const padding = 12;
  const blockHeight = lines.length * lineHeight + padding * 2;

  ensureSpace(ctx, blockHeight + 8);

  ctx.currentY += 4;

  // Draw background with border (matching nova-dashboard-900 style)
  ctx.pdf.setFillColor('#f8f9fa');
  ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
  ctx.pdf.roundedRect(
    ctx.margin.left + indent,
    ctx.currentY - 2,
    ctx.contentWidth - indent,
    blockHeight,
    4,
    4,
    'FD'
  );

  ctx.currentY += padding;
  ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
  ctx.pdf.setTextColor('#374151');
  ctx.pdf.setFont('courier', 'normal');

  for (const line of lines) {
    const maxWidth = ctx.contentWidth - indent - padding * 2;
    const wrappedLines = ctx.pdf.splitTextToSize(line || ' ', maxWidth);
    for (const wrappedLine of wrappedLines) {
      ctx.pdf.text(wrappedLine, ctx.margin.left + indent + padding, ctx.currentY);
      ctx.currentY += lineHeight;
    }
  }

  ctx.pdf.setFont('helvetica', 'normal');
  ctx.currentY += padding + 4;
}

// Render a message header with avatar circle (matching web design)
function renderMessageHeader(ctx: RenderContext, message: ChatMessage): void {
  const isUser = message.role === 'user';
  const label = isUser ? 'You' : 'Nebula';

  ctx.currentY += 12;
  ensureSpace(ctx, 30);

  // Draw avatar circle
  const avatarSize = 12;
  const avatarX = ctx.margin.left + avatarSize / 2;
  const avatarY = ctx.currentY - avatarSize / 2 + 2;

  ctx.pdf.setFillColor(isUser ? PDF_CONFIG.colors.primary : PDF_CONFIG.colors.secondary);
  ctx.pdf.circle(avatarX, avatarY, avatarSize / 2, 'F');

  // Draw initial letter in avatar
  ctx.pdf.setFontSize(7);
  ctx.pdf.setTextColor('#ffffff');
  ctx.pdf.setFont('helvetica', 'bold');
  const initial = isUser ? 'U' : 'N';
  ctx.pdf.text(initial, avatarX - 2, avatarY + 2);

  // Role label next to avatar
  ctx.pdf.setFontSize(PDF_CONFIG.fontSize.body);
  ctx.pdf.setFont('helvetica', 'bold');
  ctx.pdf.setTextColor(PDF_CONFIG.colors.text);
  ctx.pdf.text(label, ctx.margin.left + avatarSize + 8, ctx.currentY);

  ctx.pdf.setFont('helvetica', 'normal');
  ctx.currentY += 18;
}

// Render table data to PDF
function renderTable(
  ctx: RenderContext,
  data: Record<string, unknown>[],
  title?: string
): void {
  if (!data || data.length === 0) return;

  const columns = Object.keys(data[0]);
  const maxRows = 20; // Limit rows in PDF
  const rowData = data.slice(0, maxRows);

  // Table configuration (matching web design)
  const cellPadding = 6;
  const headerHeight = 24;
  const rowHeight = 20;
  const borderRadius = 4;
  const colWidth = ctx.contentWidth / columns.length;
  const tableWidth = ctx.contentWidth;

  // Check if table fits, otherwise add page
  const tableHeight = headerHeight + rowData.length * rowHeight + 20;
  ensureSpace(ctx, tableHeight);

  if (title) {
    ctx.pdf.setFontSize(PDF_CONFIG.fontSize.body + 1);
    ctx.pdf.setFont('helvetica', 'bold');
    ctx.pdf.setTextColor(PDF_CONFIG.colors.text);
    ctx.pdf.text(title, ctx.margin.left, ctx.currentY);
    ctx.pdf.setFont('helvetica', 'normal');
    ctx.currentY += 16;
  }

  // Draw table border/background
  ctx.pdf.setFillColor(PDF_CONFIG.colors.cardBg);
  ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
  ctx.pdf.roundedRect(ctx.margin.left, ctx.currentY, tableWidth, headerHeight + rowData.length * rowHeight, borderRadius, borderRadius, 'FD');

  // Draw header background
  ctx.pdf.setFillColor(PDF_CONFIG.colors.tableHeader);
  ctx.pdf.roundedRect(ctx.margin.left, ctx.currentY, tableWidth, headerHeight, borderRadius, borderRadius, 'F');
  ctx.pdf.rect(ctx.margin.left, ctx.currentY + headerHeight - borderRadius, tableWidth, borderRadius, 'F');

  // Draw header text
  ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
  ctx.pdf.setFont('helvetica', 'bold');
  ctx.pdf.setTextColor(PDF_CONFIG.colors.text);

  columns.forEach((col, i) => {
    const x = ctx.margin.left + i * colWidth + cellPadding;
    const displayCol = col.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
    const maxChars = Math.floor((colWidth - cellPadding * 2) / 4);
    const truncated = displayCol.length > maxChars ? displayCol.slice(0, maxChars) + '...' : displayCol;
    ctx.pdf.text(truncated, x, ctx.currentY + headerHeight / 2 + 3);
  });

  // Draw header bottom border
  ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
  ctx.pdf.line(ctx.margin.left, ctx.currentY + headerHeight, ctx.margin.left + tableWidth, ctx.currentY + headerHeight);

  ctx.currentY += headerHeight;

  // Draw rows
  ctx.pdf.setFont('helvetica', 'normal');

  rowData.forEach((row, rowIndex) => {
    // Draw row bottom border
    if (rowIndex < rowData.length - 1) {
      ctx.pdf.setDrawColor(PDF_CONFIG.colors.border);
      ctx.pdf.line(ctx.margin.left, ctx.currentY + rowHeight, ctx.margin.left + tableWidth, ctx.currentY + rowHeight);
    }

    ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
    ctx.pdf.setTextColor(PDF_CONFIG.colors.text);

    columns.forEach((col, i) => {
      const x = ctx.margin.left + i * colWidth + cellPadding;
      let value = row[col];

      if (typeof value === 'number') {
        value = formatNumber(value);
      } else if (value === null || value === undefined) {
        value = '-';
      }

      const displayValue = String(value);
      const maxChars = Math.floor((colWidth - cellPadding * 2) / 4);
      const truncated = displayValue.length > maxChars ? displayValue.slice(0, maxChars) + '...' : displayValue;
      ctx.pdf.text(truncated, x, ctx.currentY + rowHeight / 2 + 3);
    });

    ctx.currentY += rowHeight;
  });

  if (data.length > maxRows) {
    ctx.currentY += 8;
    ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
    ctx.pdf.setTextColor(PDF_CONFIG.colors.muted);
    ctx.pdf.text(`Showing ${maxRows} of ${data.length} rows`, ctx.margin.left, ctx.currentY);
  }

  ctx.currentY += 10;
}

// Capture and embed chart image
export async function captureChart(
  element: HTMLElement,
  ctx: RenderContext
): Promise<void> {
  try {
    const canvas = await html2canvas(element, {
      allowTaint: true,
      useCORS: true,
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
    } as Parameters<typeof html2canvas>[1]);

    const imgData = canvas.toDataURL('image/png');
    const aspectRatio = canvas.height / canvas.width;

    // Max width is content width, max height is 300pt
    const maxWidth = Math.min(ctx.contentWidth, 500);
    const maxHeight = 300;

    let imgWidth = maxWidth;
    let imgHeight = imgWidth * aspectRatio;

    if (imgHeight > maxHeight) {
      imgHeight = maxHeight;
      imgWidth = imgHeight / aspectRatio;
    }

    ensureSpace(ctx, imgHeight + 20);

    ctx.pdf.addImage(
      imgData,
      'PNG',
      ctx.margin.left + (ctx.contentWidth - imgWidth) / 2,
      ctx.currentY,
      imgWidth,
      imgHeight
    );

    ctx.currentY += imgHeight + 15;
  } catch (error) {
    console.error('Failed to capture chart:', error);
    addWrappedText(ctx, '[Chart could not be captured]', PDF_CONFIG.fontSize.small, PDF_CONFIG.colors.muted);
  }
}

// Embed a pre-captured chart image (PNG data URL) into the PDF
async function embedChartImage(imgData: string, ctx: RenderContext): Promise<void> {
  try {
    // Load image to get dimensions
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = imgData;
    });

    const aspectRatio = img.height / img.width;
    const maxWidth = Math.min(ctx.contentWidth, 500);
    const maxHeight = 300;

    let imgWidth = maxWidth;
    let imgHeight = imgWidth * aspectRatio;

    if (imgHeight > maxHeight) {
      imgHeight = maxHeight;
      imgWidth = imgHeight / aspectRatio;
    }

    ensureSpace(ctx, imgHeight + 20);

    ctx.pdf.addImage(
      imgData,
      'PNG',
      ctx.margin.left + (ctx.contentWidth - imgWidth) / 2,
      ctx.currentY,
      imgWidth,
      imgHeight
    );

    ctx.currentY += imgHeight + 15;
  } catch (error) {
    console.error('Failed to embed chart image:', error);
    addWrappedText(ctx, '[Chart could not be embedded]', PDF_CONFIG.fontSize.small, PDF_CONFIG.colors.muted);
  }
}

// Render citations appendix
function renderCitations(ctx: RenderContext, citations: Citation[]): void {
  if (!citations || citations.length === 0) return;

  ctx.pdf.addPage();
  ctx.currentY = ctx.margin.top;

  ctx.pdf.setFontSize(PDF_CONFIG.fontSize.heading);
  ctx.pdf.setTextColor(PDF_CONFIG.colors.text);
  ctx.pdf.text('Citations', ctx.margin.left, ctx.currentY);
  ctx.currentY += 20;

  citations.forEach((citation, index) => {
    ensureSpace(ctx, 40);

    ctx.pdf.setFontSize(PDF_CONFIG.fontSize.body);
    ctx.pdf.setTextColor(PDF_CONFIG.colors.primary);
    ctx.pdf.text(`[${index + 1}] ${citation.title}`, ctx.margin.left, ctx.currentY);
    ctx.currentY += 12;

    ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
    ctx.pdf.setTextColor(PDF_CONFIG.colors.muted);
    addWrappedText(ctx, citation.url, PDF_CONFIG.fontSize.small, PDF_CONFIG.colors.muted, 10);

    if (citation.text) {
      ctx.currentY += 4;
      addWrappedText(ctx, citation.text, PDF_CONFIG.fontSize.small, PDF_CONFIG.colors.text, 10);
    }

    ctx.currentY += 10;
  });
}

// Main PDF generation function
// Load image and get dimensions for PDF embedding
async function loadImageForPdf(url: string): Promise<{ data: string; width: number; height: number } | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Get actual image dimensions
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = base64;
    });

    return { data: base64, width: img.width, height: img.height };
  } catch {
    return null;
  }
}

export async function generatePdf(
  messages: ChatMessage[],
  title: string,
  options: PdfExportOptions = {},
  onProgress?: (current: number, total: number, status: string) => void,
  renderContainer?: HTMLElement | null,
  chartImages?: string[]
): Promise<Blob> {
  const {
    includeThinkingContent = false,
    includeCitations = true,
    pageSize = 'a4',
  } = options;

  const pageConfig = PDF_CONFIG[pageSize];
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: pageSize,
  });

  const ctx: RenderContext = {
    pdf,
    pageWidth: pageConfig.width,
    pageHeight: pageConfig.height,
    contentWidth: pageConfig.width - PDF_CONFIG.margin.left - PDF_CONFIG.margin.right,
    margin: PDF_CONFIG.margin,
    currentY: PDF_CONFIG.margin.top,
  };

  // Collect all citations
  const allCitations: Citation[] = [];

  // Track which pre-captured chart image to use next
  let chartImageIndex = 0;

  // Load and add NOVA logo (use high-res version for crisp rendering)
  const logoUrl = '/assets/images/nova-new-logo.png';
  const logoData = await loadImageForPdf(logoUrl);

  if (logoData) {
    // Scale logo to desired height while maintaining aspect ratio
    const targetHeight = 20;
    const aspectRatio = logoData.width / logoData.height;
    const targetWidth = targetHeight * aspectRatio;

    pdf.addImage(logoData.data, 'PNG', ctx.margin.left, ctx.currentY - 5, targetWidth, targetHeight);
    ctx.currentY += targetHeight + 20;
  }

  // Header - Title (with word wrapping for long titles)
  pdf.setFont('helvetica', 'bold');
  addWrappedText(ctx, title || 'Nebula Conversation', PDF_CONFIG.fontSize.title, PDF_CONFIG.colors.text);
  pdf.setFont('helvetica', 'normal');
  ctx.currentY += 4;

  // Export date
  pdf.setFontSize(PDF_CONFIG.fontSize.small);
  pdf.setTextColor(PDF_CONFIG.colors.muted);
  pdf.text(`Exported on ${formatTimestamp(new Date())}`, ctx.margin.left, ctx.currentY);
  ctx.currentY += 20;

  // Separator line
  pdf.setDrawColor(PDF_CONFIG.colors.border);
  pdf.line(ctx.margin.left, ctx.currentY, ctx.margin.left + ctx.contentWidth, ctx.currentY);
  ctx.currentY += 30;

  // Count Q&A pairs (user messages count as pairs)
  const totalPairs = messages.filter(m => m.role === 'user').length;
  let currentPair = 0;

  // Process each message
  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];

    // Update progress when we hit a user message (start of a new Q&A pair)
    if (message.role === 'user') {
      currentPair++;
      onProgress?.(currentPair, totalPairs, `Processing ${currentPair} of ${totalPairs}`);
    }

    // User messages: render inline as "You: content"
    if (message.role === 'user') {
      ctx.currentY += 12;
      ensureSpace(ctx, 20);

      // Draw avatar circle
      const avatarSize = 12;
      const avatarX = ctx.margin.left + avatarSize / 2;
      const avatarY = ctx.currentY - avatarSize / 2 + 2;
      ctx.pdf.setFillColor(PDF_CONFIG.colors.primary);
      ctx.pdf.circle(avatarX, avatarY, avatarSize / 2, 'F');
      ctx.pdf.setFontSize(7);
      ctx.pdf.setTextColor('#ffffff');
      ctx.pdf.setFont('helvetica', 'bold');
      ctx.pdf.text('U', avatarX - 2, avatarY + 2);

      // Render "You: content" inline after avatar
      const userContent = message.content || '';
      const textIndent = avatarSize + 8;
      renderFormattedLine(ctx, `**You:** ${userContent}`, PDF_CONFIG.fontSize.body, PDF_CONFIG.colors.text, textIndent);
      ctx.pdf.setFont('helvetica', 'normal');

      ctx.currentY += 10;
      continue;
    }

    renderMessageHeader(ctx, message);

    // Thinking content (optional)
    if (includeThinkingContent && message.thinkingContent) {
      ctx.pdf.setFillColor('#fef3c7');
      const thinkingHeight = 40;
      ensureSpace(ctx, thinkingHeight);
      ctx.pdf.roundedRect(ctx.margin.left, ctx.currentY, ctx.contentWidth, thinkingHeight, 3, 3, 'F');

      ctx.pdf.setFontSize(PDF_CONFIG.fontSize.small);
      ctx.pdf.setTextColor('#92400e');
      ctx.pdf.text('Thinking...', ctx.margin.left + 8, ctx.currentY + 15);
      ctx.pdf.setTextColor('#b45309');
      const thinkingPreview = message.thinkingContent.slice(0, 100) + (message.thinkingContent.length > 100 ? '...' : '');
      ctx.pdf.text(thinkingPreview, ctx.margin.left + 8, ctx.currentY + 28);
      ctx.currentY += thinkingHeight + 8;
    }

    // Content blocks
    if (message.contentBlocks && message.contentBlocks.length > 0) {
      for (const block of message.contentBlocks) {
        if (block.type === 'text') {
          // Clean orphaned markdown markers (e.g. "**" left when a visualization
          // splits "**[CHART] TITLE**" into text:"**", viz, text:"TITLE**")
          let textContent = block.content;
          const boldMarkers = textContent.match(/\*\*/g);
          if (boldMarkers && boldMarkers.length % 2 !== 0) {
            textContent = textContent.replace(/^\s*\*\*\s*/, '').replace(/\s*\*\*\s*$/, '');
          }
          if (textContent.trim()) {
            renderMarkdownContent(ctx, textContent);
          }
        } else if (block.type === 'visualization') {
          // Use pre-captured chart image if available, otherwise fall back to container capture
          const isChart = block.data.config.type !== 'table' && block.data.config.type !== 'summary_card';
          const preCapture = isChart && chartImages && chartImageIndex < chartImages.length
            ? chartImages[chartImageIndex++]
            : null;
          await renderVisualizationToPdf(ctx, block.data, renderContainer || null, preCapture);
        }
      }
    } else if (message.content) {
      // Fallback to raw content
      renderMarkdownContent(ctx, message.content);
    }

    // Collect citations
    if (includeCitations && message.citations) {
      allCitations.push(...message.citations);
    }

    ctx.currentY += 10;
  }

  // Add citations appendix
  if (includeCitations && allCitations.length > 0) {
    renderCitations(ctx, allCitations);
  }

  // Add footer to all pages
  const totalPages = pdf.getNumberOfPages();
  const footerText = 'Nebula is AI and can make mistakes. Please double-check responses.';

  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(PDF_CONFIG.fontSize.small);
    pdf.setTextColor(PDF_CONFIG.colors.muted);
    pdf.setFont('helvetica', 'italic');

    // Center the footer text
    const textWidth = pdf.getTextWidth(footerText);
    const xPos = (ctx.pageWidth - textWidth) / 2;
    const yPos = ctx.pageHeight - 20;

    pdf.text(footerText, xPos, yPos);
  }

  pdf.setFont('helvetica', 'normal');

  return pdf.output('blob');
}

// Render visualization by capturing from DOM
async function renderVisualizationToPdf(
  ctx: RenderContext,
  vizData: VisualizationData,
  container: HTMLElement | null,
  preCapturedImage?: string | null
): Promise<void> {
  // For tables, render directly
  if (vizData.config.type === 'table') {
    renderTable(ctx, vizData.data, vizData.config.title);
    return;
  }

  // For summary cards, render as a simple list
  if (vizData.config.type === 'summary_card') {
    if (vizData.config.title) {
      addWrappedText(ctx, vizData.config.title, PDF_CONFIG.fontSize.body + 1, PDF_CONFIG.colors.text);
      ctx.currentY += 8;
    }

    for (const item of vizData.data) {
      const keys = Object.keys(item);
      const labelKey = keys.find((k) => typeof item[k] === 'string') || keys[0];
      const valueKey = keys.find((k) => typeof item[k] === 'number') || keys[1];
      const label = String(item[labelKey] || 'Metric');
      const value = typeof item[valueKey] === 'number' ? formatNumber(item[valueKey] as number) : String(item[valueKey]);
      addWrappedText(ctx, `${label}: ${value}`, PDF_CONFIG.fontSize.body, PDF_CONFIG.colors.text, 10);
    }

    ctx.currentY += 10;
    return;
  }

  // For charts, use pre-captured image if available (fixes duplicate chart bug)
  if (preCapturedImage) {
    await embedChartImage(preCapturedImage, ctx);
    return;
  }

  // Fallback: try to capture from live render container
  if (container) {
    const chartElement = container.querySelector('[data-pdf-chart]') as HTMLElement;
    if (chartElement) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      await captureChart(chartElement, ctx);
      return;
    }
  }

  // Final fallback: text placeholder
  addWrappedText(
    ctx,
    `[${vizData.config.type}: ${vizData.config.title || 'Chart'}]`,
    PDF_CONFIG.fontSize.small,
    PDF_CONFIG.colors.muted
  );
  ctx.currentY += 10;
}
